import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';

export const useVoiceRecording = () => {
	const [recordingDuration, setRecordingDuration] = useState(0);
	const [recordingPaused, setRecordingPaused] = useState(false);
	const [isPreviewingRecording, setIsPreviewingRecording] = useState(false);
	const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const isCancellingRef = useRef(false);

	// Use ref instead of state for callback to avoid stale closures
	const onVoiceMessageRef = useRef<((blob: Blob) => void) | null>(null);

	// Determine the best audio format supported by the browser - memoized to prevent excessive logging
	const supportedFormat = useMemo(() => {
		const getSupportedAudioFormat = () => {
			if (typeof MediaRecorder !== 'undefined') {
				// Try MP4 first (best server compatibility)
				if (MediaRecorder.isTypeSupported('audio/mp4')) {
					return 'audio/mp4';
				}
				// Fallback to WebM with Opus (widely supported)
				if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
					return 'audio/webm;codecs=opus';
				}
				// Last resort: WebM without codec specification
				if (MediaRecorder.isTypeSupported('audio/webm')) {
					return 'audio/webm';
				}
			}
			// If MediaRecorder is not available or no formats supported, return undefined
			return undefined;
		};

		const format = getSupportedAudioFormat();

		return format;
	}, []); // Empty dependency array means this only runs once

	const { status, startRecording, stopRecording, mediaBlobUrl } = useReactMediaRecorder({
		audio: true,
		mediaRecorderOptions: supportedFormat
			? {
					mimeType: supportedFormat,
				}
			: undefined, // Let the browser choose if no specific format is supported
		onStop: (blobUrl, blob) => {
			// Skip processing if we're cancelling
			if (!isCancellingRef.current && onVoiceMessageRef.current) {
				onVoiceMessageRef.current(blob);
			}
			isCancellingRef.current = false;
		},
	});

	const isRecording = status === 'recording' || recordingPaused;

	// Format recording time as mm:ss
	const formatRecordingTime = useCallback((seconds: number) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
	}, []);

	// Handle starting a voice recording
	const handleStartRecording = useCallback(async () => {
		try {
			setRecordingDuration(0);
			setIsPreviewingRecording(false);

			// Request microphone permission first
			await navigator.mediaDevices.getUserMedia({ audio: true });

			// Start recording
			startRecording();
		} catch (error) {
			console.error('Failed to start recording:', error);
			alert('Could not access microphone. Please check your permissions.');
		}
	}, [startRecording]);

	// Handle stopping a voice recording
	const handleStopRecording = useCallback(() => {
		stopRecording();
	}, [stopRecording]);

	// Handle canceling a voice recording
	const handleCancelRecording = useCallback(() => {
		if (recordingTimerRef.current) {
			clearInterval(recordingTimerRef.current);
			recordingTimerRef.current = null;
		}

		isCancellingRef.current = true;
		setRecordingDuration(0);
		setIsPreviewingRecording(false);

		if (status === 'recording' || status === 'paused') {
			stopRecording();
		}

		if (mediaBlobUrl) {
			URL.revokeObjectURL(mediaBlobUrl);
		}
	}, [status, stopRecording, mediaBlobUrl]);

	// Handle previewing the recorded audio
	const handlePreviewRecording = useCallback(() => {
		if (audioRef.current && mediaBlobUrl) {
			if (audioRef.current.paused) {
				audioRef.current.play();
			} else {
				audioRef.current.pause();
			}
		}
	}, [mediaBlobUrl]);

	// Convert blob to file and process
	const processVoiceMessage = useCallback(async (blob: Blob) => {
		if (!blob) return null;

		const now = new Date();
		const dateString = now.toISOString().replace(/[:.]/g, '-');

		// Use MP3 format for better server compatibility
		let extension = 'mp3';
		let mimeType = 'audio/mpeg';

		// If the blob is already in a different audio format, keep it
		if (blob.type && blob.type.startsWith('audio/')) {
			mimeType = blob.type;
			const mimeExtension = blob.type.split('/')[1];
			if (mimeExtension === 'mp4') {
				extension = 'm4a'; // Use m4a extension for mp4 audio
			} else if (mimeExtension === 'mpeg') {
				extension = 'mp3';
			} else if (mimeExtension) {
				extension = mimeExtension;
			}
		}

		const fileName = `voice-message-${dateString}.${extension}`;

		// Create file with explicit audio MIME type
		const file = new File([blob], fileName, {
			type: mimeType,
			lastModified: now.getTime(),
		});

		return file;
	}, []);

	// Handle recording timer
	useEffect(() => {
		if (status === 'recording' && !recordingPaused) {
			// Start timer immediately, then every second
			recordingTimerRef.current = setInterval(() => {
				setRecordingDuration((prev) => prev + 1);
			}, 1000);
		} else if (recordingPaused) {
			if (recordingTimerRef.current) {
				clearInterval(recordingTimerRef.current);
			}
		} else {
			if (recordingTimerRef.current) {
				clearInterval(recordingTimerRef.current);
				recordingTimerRef.current = null;
			}

			if (!isPreviewingRecording) {
				setRecordingDuration(0);
			}
		}

		return () => {
			if (recordingTimerRef.current) {
				clearInterval(recordingTimerRef.current);
			}
		};
	}, [status, recordingPaused, isPreviewingRecording]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (recordingTimerRef.current) {
				clearInterval(recordingTimerRef.current);
			}
		};
	}, []);

	// Function to set the voice message callback
	const setOnVoiceMessage = useCallback((callback: ((blob: Blob) => void) | null) => {
		onVoiceMessageRef.current = callback;
	}, []);

	return {
		isRecording,
		recordingDuration,
		recordingPaused,
		isPreviewingRecording,
		mediaBlobUrl,
		audioRef,
		status,
		formatRecordingTime,
		handleStartRecording,
		handleStopRecording,
		handleCancelRecording,
		handlePreviewRecording,
		processVoiceMessage,
		setOnVoiceMessage,
	};
};
